/**
 * @public — visualizes the per-step status of a bootstrap run.
 *
 * The bootstrap runs per scope (cross + each discipline); for each scope it
 * fires one signal-matrix call then N axis-prompt calls. This component takes
 * the `BootstrapResult` shape the orchestrator returns (`{ scopes }`) and
 * renders a check / error / "in-flight" state per step, grouped by scope.
 *
 * "In-flight" is a transient UI state — the orchestrator is synchronous, so by
 * the time we have a `result`, every step is in either `ok` or `error`. The
 * `running` prop lets the caller render a spinning state DURING the call.
 */

import type { BootstrapResult, LevelAssessmentModel } from "@shared/labs/level-assessment-model";

/** One scope's axes for rendering the pending/in-flight skeleton. */
export interface BootstrapScopeSpec {
  /** Scope key — `"cross"` or a discipline id. */
  key: string;
  /** Display label — e.g. "Cross" or the discipline's label. */
  label: string;
  axes: { id: string; label: string }[];
}

/** Build the scope list (cross + each discipline) from a loaded model. */
export function scopesFromModel(
  model: Pick<LevelAssessmentModel, "crossAxes" | "disciplines">,
): BootstrapScopeSpec[] {
  return [
    { key: "cross", label: "Cross", axes: model.crossAxes },
    ...model.disciplines.map((d) => ({ key: d.id, label: d.label, axes: d.axes })),
  ];
}

/** True when every scope's matrix + all its axes succeeded. */
export function isBootstrapFullSuccess(
  result: BootstrapResult,
  scopes: BootstrapScopeSpec[],
): boolean {
  return scopes.every((s) => {
    const r = result.scopes[s.key];
    return !!r?.matrixOk && r.okAxes.length === s.axes.length;
  });
}

export interface BootstrapProgressProps {
  /** When true, every step shows as "running" — used during the actual
   *  RPC call. Once it resolves, pass the result here and clear `running`. */
  running: boolean;
  /** Result returned by `labsBootstrapModel` or `labsCreateModel.bootstrap`.
   *  Null when there's no run yet or the call hasn't returned. */
  result: BootstrapResult | null;
  /** The scopes the model has — used to render rows. */
  scopes: BootstrapScopeSpec[];
}

type StepStatus = "pending" | "running" | "ok" | "error";

interface StepRow {
  label: string;
  status: StepStatus;
  error?: string;
}

function buildSteps(
  result: BootstrapResult | null,
  running: boolean,
  scopes: BootstrapScopeSpec[],
): StepRow[] {
  const rows: StepRow[] = [];
  for (const scope of scopes) {
    const scopeResult = result?.scopes[scope.key] ?? null;
    const matrixStatus: StepStatus = running
      ? "running"
      : scopeResult === null
        ? "pending"
        : scopeResult.matrixOk
          ? "ok"
          : "error";
    rows.push({
      label: `Matriz de sinais · ${scope.label}`,
      status: matrixStatus,
      error: scopeResult?.matrixError ?? undefined,
    });
    for (const axis of scope.axes) {
      const label = `Prompt ${scope.label} · ${axis.label}`;
      if (running) {
        rows.push({ label, status: "running" });
      } else if (scopeResult === null) {
        rows.push({ label, status: "pending" });
      } else if (scopeResult.okAxes.includes(axis.id)) {
        rows.push({ label, status: "ok" });
      } else if (scopeResult.errorAxes[axis.id]) {
        rows.push({ label, status: "error", error: scopeResult.errorAxes[axis.id] });
      } else {
        rows.push({ label, status: "pending" });
      }
    }
  }
  return rows;
}

function statusColor(status: StepStatus): string {
  switch (status) {
    case "ok":
      return "var(--r-forest, #1d9e75)";
    case "error":
      return "var(--r-coral, #f3354b)";
    case "running":
      return "var(--r-amber, #b07811)";
    default:
      return "var(--r-ink-faint, #c7c2bb)";
  }
}

function StatusGlyph({ status }: { status: StepStatus }) {
  const color = statusColor(status);
  if (status === "ok") {
    return (
      <span
        aria-hidden
        style={{
          display: "inline-block",
          width: 16,
          height: 16,
          borderRadius: "50%",
          background: color,
          color: "#fff",
          textAlign: "center",
          lineHeight: "16px",
          fontSize: 11,
          fontWeight: 700,
        }}
      >
        ✓
      </span>
    );
  }
  if (status === "error") {
    return (
      <span
        aria-hidden
        style={{
          display: "inline-block",
          width: 16,
          height: 16,
          borderRadius: "50%",
          background: color,
          color: "#fff",
          textAlign: "center",
          lineHeight: "16px",
          fontSize: 11,
          fontWeight: 700,
        }}
      >
        !
      </span>
    );
  }
  if (status === "running") {
    return (
      <span
        aria-hidden
        style={{
          display: "inline-block",
          width: 12,
          height: 12,
          borderRadius: "50%",
          border: `2px solid ${color}`,
          borderRightColor: "transparent",
          animation: "spin 0.9s linear infinite",
        }}
      />
    );
  }
  return (
    <span
      aria-hidden
      style={{
        display: "inline-block",
        width: 12,
        height: 12,
        borderRadius: "50%",
        border: `1.5px dashed ${color}`,
      }}
    />
  );
}

/** Full count + state summary shown above the step list. Hides until the
 *  user has either started a run or has a result. */
function Summary({
  result,
  running,
  totalSteps,
}: {
  result: BootstrapResult | null;
  running: boolean;
  totalSteps: number;
}) {
  if (running) {
    return (
      <div style={{ fontSize: 12, color: "var(--r-ink-muted)" }}>
        Disparando {totalSteps} chamadas LLM (Opus). Pode levar 30–60s.
      </div>
    );
  }
  if (!result) return null;
  const okCount = Object.values(result.scopes).reduce(
    (acc, s) => acc + (s.matrixOk ? 1 : 0) + s.okAxes.length,
    0,
  );
  const errorCount = totalSteps - okCount;
  if (errorCount === 0) {
    return (
      <div style={{ fontSize: 12, color: "var(--r-forest, #1d9e75)", fontWeight: 500 }}>
        Bootstrap completo · {okCount}/{totalSteps} steps OK
      </div>
    );
  }
  return (
    <div style={{ fontSize: 12, color: "var(--r-amber, #b07811)", fontWeight: 500 }}>
      Bootstrap parcial · {okCount}/{totalSteps} OK · {errorCount} com erro
    </div>
  );
}

export function BootstrapProgress({ running, result, scopes }: BootstrapProgressProps) {
  const steps = buildSteps(result, running, scopes);

  return (
    <div data-testid="bootstrap-progress">
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <Summary result={result} running={running} totalSteps={steps.length} />
      <ul
        style={{
          listStyle: "none",
          margin: "10px 0 0",
          padding: 0,
          display: "flex",
          flexDirection: "column",
          gap: 8,
        }}
      >
        {steps.map((step, i) => (
          <li
            // biome-ignore lint/suspicious/noArrayIndexKey: step list is static.
            key={i}
            data-testid={`bootstrap-step-${step.status}`}
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 10,
              fontSize: 13,
              color: step.status === "error" ? "var(--r-ink)" : "var(--r-ink-muted)",
            }}
          >
            <span style={{ marginTop: 2 }}>
              <StatusGlyph status={step.status} />
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: "var(--r-ink)" }}>{step.label}</div>
              {step.error && (
                <div
                  style={{
                    fontSize: 12,
                    color: "var(--r-coral, #f3354b)",
                    marginTop: 2,
                    lineHeight: 1.45,
                    fontFamily: "ui-monospace, SFMono-Regular, monospace",
                    wordBreak: "break-word",
                  }}
                >
                  {step.error}
                </div>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
